import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, IconButton, Checkbox, TextField, Snackbar, Alert, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Skeleton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  SalonMember, TaskCategory, TaskDetail, TaskStatus, taskApi, taskErrorCode, taskErrorMessage,
} from '../../services/taskService';
import { useAuthStore } from '../../store/authStore';
import { useTaskTimerStore } from '../../store/taskTimerStore';
import {
  PRIORITY_LABEL, PRIORITY_TONE, STATUS_COLOR, STATUS_LABEL, STATUS_TONE, formatDate, formatDateTime,
  formatDueLabel, userLabel,
} from './taskShared';
import TaskTimerPanel from './TaskTimerPanel';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import TaskHistory, { withMemberName } from './TaskHistory';
import TaskFormDialog from './TaskFormDialog';
import {
  PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar,
} from '../../components/ui';
import { tokens } from '../../theme';

const GAP = { xs: 2, md: 2.5 };

/** Dwie kolumny od lg; wezsze ekrany — jedna kolumna: tresc, panel szczegolow, rozmowa i historia. */
const layoutSx = {
  display: 'grid',
  gap: GAP,
  alignItems: 'start',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' },
  gridTemplateAreas: { xs: '"top" "meta" "bottom"', lg: '"top meta" "bottom meta"' },
  gridTemplateRows: { lg: 'auto 1fr' },
} as const;

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" columnGap={2} rowGap={0.25} flexWrap="wrap">
    <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ fontSize: '0.875rem', fontWeight: 600, color: tokens.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums', minWidth: 0, ml: 'auto' }}>
      {children}
    </Box>
  </Stack>
);

export const TaskDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const refreshTimer = useTaskTimerStore((s) => s.refresh);

  const [task, setTask] = useState<TaskDetail | null>(null);
  const [members, setMembers] = useState<SalonMember[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [statusMenu, setStatusMenu] = useState<null | HTMLElement>(null);
  const [reworkDialog, setReworkDialog] = useState<{ reason: string } | null>(null);
  const [timerDialog, setTimerDialog] = useState<TaskStatus | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [historyKey, setHistoryKey] = useState(0);

  const notify = useCallback((msg: string, severity: 'success' | 'error' | 'info') => {
    setToast({ msg, severity });
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const detail = await taskApi.get(id);
      setTask(detail);
      const [mem, cats] = await Promise.all([
        taskApi.members(detail.salonId),
        taskApi.listCategories(detail.salonId),
      ]);
      setMembers(mem);
      setCategories(cats);
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się wczytać zadania.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshTimer(); }, [refreshTimer]);

  const refreshAll = useCallback(() => {
    load();
    setHistoryKey((k) => k + 1);
    refreshTimer();
  }, [load, refreshTimer]);

  const changeStatus = async (status: TaskStatus, options: { reason?: string; stopTimer?: boolean } = {}) => {
    if (!task) return;
    setStatusMenu(null);
    if (status === 'NEEDS_REWORK' && !options.reason) {
      setReworkDialog({ reason: '' });
      return;
    }
    try {
      await taskApi.changeStatus(task.id, status, options);
      notify(`Status zmieniony na „${STATUS_LABEL[status]}”.`, 'success');
      refreshAll();
    } catch (err) {
      if (taskErrorCode(err) === 'ACTIVE_TIMER') {
        setTimerDialog(status);
        return;
      }
      notify(taskErrorMessage(err, 'Nie udało się zmienić statusu.'), 'error');
    }
  };

  const toggleChecklist = async (itemId: string, isDone: boolean) => {
    try {
      await taskApi.updateChecklistItem(itemId, { isDone });
      refreshAll();
    } catch (err) {
      notify(taskErrorMessage(err, 'Nie udało się zaktualizować checklisty.'), 'error');
    }
  };

  const addChecklistItem = async () => {
    if (!task || !newChecklistItem.trim()) return;
    try {
      await taskApi.addChecklistItem(task.id, newChecklistItem.trim());
      setNewChecklistItem('');
      refreshAll();
    } catch (err) {
      notify(taskErrorMessage(err, 'Nie udało się dodać pozycji.'), 'error');
    }
  };

  const removeChecklistItem = async (itemId: string) => {
    try {
      await taskApi.deleteChecklistItem(itemId);
      refreshAll();
    } catch (err) {
      notify(taskErrorMessage(err, 'Nie udało się usunąć pozycji.'), 'error');
    }
  };

  /**
   * Attachments are private: they are fetched with the auth header and opened
   * from a temporary blob URL, so no public link to the file ever exists.
   */
  const openAttachment = async (attachmentId: string, isImage: boolean, name: string) => {
    try {
      const blob = await taskApi.fetchAttachmentBlob(attachmentId, isImage);
      const url = URL.createObjectURL(blob);
      if (isImage) {
        window.open(url, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      notify(taskErrorMessage(err, 'Nie udało się pobrać pliku.'), 'error');
    }
  };

  const breadcrumbs = [{ label: 'Zadania', to: '/tasks' }];

  if (loading && !task) {
    return (
      <Box aria-busy="true" aria-label="Ładowanie zadania">
        <PageHeader breadcrumbs={breadcrumbs} title={<Skeleton width="55%" />} description={<Skeleton width={260} />} />
        <Box sx={layoutSx}>
          <Stack gap={GAP} sx={{ gridArea: 'top', minWidth: 0 }}>
            <LoadingState variant="block" height={120} />
            <LoadingState variant="table" rows={3} />
          </Stack>
          <Box sx={{ gridArea: 'meta', minWidth: 0 }}>
            <LoadingState variant="block" height={300} />
          </Box>
        </Box>
      </Box>
    );
  }
  if (error || !task) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Nie udało się otworzyć zadania" />
        <SectionCard>
          <EmptyState
            icon={<TaskAltIcon />}
            title={error || 'Zadanie nie istnieje.'}
            description="Zadanie mogło zostać usunięte albo nie masz do niego dostępu. Odśwież stronę lub wróć do listy zadań."
            actions={(
              <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')}>
                Wróć do zadań
              </Button>
            )}
          />
        </SectionCard>
      </Box>
    );
  }

  const p = task.permissions;
  const canSubmitReview = p.availableStatuses.includes('IN_REVIEW');
  const canSubmitApproval = p.availableStatuses.includes('IN_APPROVAL');
  const canComplete = p.availableStatuses.includes('DONE');
  const canRework = p.canApprove && p.availableStatuses.includes('NEEDS_REWORK');
  const isPending = task.status === 'IN_REVIEW' || task.status === 'IN_APPROVAL';
  const checklistPercent = task.checklistProgress.percent ?? 0;
  const assignees = task.assignees.map((a) => withMemberName(a, members));
  const dueLabel = task.dueAt ? formatDueLabel(task.dueAt, task.dueHasTime) : '';

  // Kolejnosc: najpierw krok, ktory zamyka lub posuwa zadanie dalej — on jest przyciskiem glownym.
  const workflow = [
    canComplete && {
      key: 'DONE',
      label: isPending && p.canApprove ? 'Zaakceptuj i zamknij' : 'Oznacz jako zakończone',
      icon: <CheckCircleOutlineIcon />,
      onClick: () => changeStatus('DONE'),
    },
    canSubmitApproval && { key: 'IN_APPROVAL', label: 'Przekaż do akceptacji', icon: <SendOutlinedIcon />, onClick: () => changeStatus('IN_APPROVAL') },
    canSubmitReview && { key: 'IN_REVIEW', label: 'Przekaż do weryfikacji', icon: <SendOutlinedIcon />, onClick: () => changeStatus('IN_REVIEW') },
    canRework && { key: 'NEEDS_REWORK', label: 'Zwróć do poprawy', icon: <ReplayIcon />, onClick: () => setReworkDialog({ reason: '' }) },
  ].filter(Boolean) as { key: string; label: string; icon: React.ReactNode; onClick: () => void }[];

  const pills = (
    <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 0.75 }}>
      <StatusPill label={STATUS_LABEL[task.status]} tone={STATUS_TONE[task.status]} />
      <StatusPill label={`Priorytet: ${PRIORITY_LABEL[task.priority].toLowerCase()}`} tone={PRIORITY_TONE[task.priority]} />
      {task.dueAt && (
        task.isOverdue ? (
          <StatusPill
            tone="danger"
            label={dueLabel.includes('po terminie') ? dueLabel : `Po terminie · ${dueLabel}`}
          />
        ) : (
          <StatusPill tone="neutral" noDot label={`Termin: ${dueLabel}`} />
        )
      )}
      {task.requiresApproval && <StatusPill tone="neutral" noDot label="Wymaga akceptacji właściciela" />}
      {task.isArchived && <StatusPill tone="neutral" noDot label="W archiwum" />}
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={task.title}
        description={pills}
        actions={(p.canEdit || p.availableStatuses.length > 0) ? (
          <>
            {p.availableStatuses.length > 0 && (
              <Button variant="outlined" endIcon={<ExpandMoreIcon />} onClick={(e) => setStatusMenu(e.currentTarget)}>
                Zmień status
              </Button>
            )}
            {p.canEdit && (
              <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)}>
                Edytuj
              </Button>
            )}
          </>
        ) : undefined}
      />
      <Menu
        anchorEl={statusMenu}
        open={!!statusMenu}
        onClose={() => setStatusMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {p.availableStatuses.map((s) => (
          <MenuItem key={s} onClick={() => changeStatus(s)} sx={{ gap: 1.25 }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[s].dot, flexShrink: 0 }} />
            {STATUS_LABEL[s]}
          </MenuItem>
        ))}
      </Menu>

      {(task.status === 'NEEDS_REWORK' && task.rejectionReason) || (task.status === 'DONE' && task.approvedBy) || isPending ? (
        <Stack gap={1.5} sx={{ mb: GAP }}>
          {task.status === 'NEEDS_REWORK' && task.rejectionReason && (
            <Alert severity="warning">
              <Typography sx={{ fontWeight: 700, fontSize: 'inherit' }}>
                Zwrócone do poprawy przez {userLabel(withMemberName(task.rejectedBy, members))} · {formatDateTime(task.rejectedAt)}
              </Typography>
              <Typography sx={{ fontSize: 'inherit', mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{task.rejectionReason}</Typography>
            </Alert>
          )}
          {task.status === 'DONE' && task.approvedBy && (
            <Alert severity="success">
              Zaakceptowane przez {userLabel(withMemberName(task.approvedBy, members))} · {formatDateTime(task.approvedAt)}
            </Alert>
          )}
          {isPending && (
            <Alert severity="info">
              {task.status === 'IN_REVIEW'
                ? 'Zadanie czeka na weryfikację właściciela.'
                : 'Zadanie czeka na akceptację właściciela.'}
            </Alert>
          )}
        </Stack>
      ) : null}

      <Box sx={layoutSx}>
        <Stack gap={GAP} sx={{ gridArea: 'top', minWidth: 0 }}>
          <SectionCard title="Opis" padding="none" divider={false}>
            <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1.25, pb: { xs: 2, md: 2.5 } }}>
              {task.description ? (
                <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {task.description}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.9375rem', color: tokens.muted }}>
                  {p.canEdit ? 'Brak opisu. Dodaj go w edycji, żeby wszyscy wiedzieli, co dokładnie zrobić.' : 'Zadanie nie ma opisu.'}
                </Typography>
              )}
            </Box>
            {workflow.length > 0 && (
              <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.75, borderTop: `1px solid ${tokens.line}`, bgcolor: tokens.surface }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 1 }}>Następny krok</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {workflow.map((action, idx) => (
                    <Button
                      key={action.key}
                      variant={idx === 0 ? 'contained' : 'outlined'}
                      startIcon={action.icon}
                      onClick={action.onClick}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </SectionCard>

          <SectionCard
            title="Checklista"
            action={task.checklistProgress.total > 0 ? (
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                {task.checklistProgress.done} z {task.checklistProgress.total}
              </Typography>
            ) : undefined}
          >
            {task.checklistProgress.total > 0 && (
              <LinearProgress
                variant="determinate"
                value={checklistPercent}
                sx={{ mb: 1.25 }}
                aria-label={`Postęp checklisty ${checklistPercent}%`}
              />
            )}
            {task.checklist.length === 0 ? (
              <Typography sx={{ fontSize: '0.9375rem', color: tokens.muted, mb: p.canParticipate ? 1.5 : 0 }}>
                {p.canParticipate ? 'Brak pozycji. Rozbij zadanie na mniejsze kroki, żeby łatwiej śledzić postęp.' : 'Zadanie nie ma checklisty.'}
              </Typography>
            ) : (
              <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mx: -1, mb: p.canParticipate ? 1.25 : 0 }}>
                {task.checklist.map((item) => (
                  <Stack
                    component="li"
                    key={item.id}
                    direction="row"
                    alignItems="center"
                    sx={{ borderRadius: '10px', pr: 0.5, '&:hover': p.canParticipate ? { bgcolor: tokens.surface } : undefined }}
                  >
                    <Box
                      component="label"
                      sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, cursor: p.canParticipate ? 'pointer' : 'default', py: 0.25 }}
                    >
                      <Checkbox
                        checked={item.isDone}
                        disabled={!p.canParticipate}
                        onChange={(e) => toggleChecklist(item.id, e.target.checked)}
                      />
                      <Typography sx={{
                        fontSize: '0.9375rem',
                        wordBreak: 'break-word',
                        textDecoration: item.isDone ? 'line-through' : 'none',
                        color: item.isDone ? tokens.muted : tokens.ink,
                      }}
                      >
                        {item.content}
                      </Typography>
                    </Box>
                    {p.canParticipate && (
                      <Tooltip title="Usuń pozycję">
                        <IconButton
                          size="small"
                          aria-label={`Usuń ${item.content}`}
                          onClick={() => removeChecklistItem(item.id)}
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
            {p.canParticipate && (
              <Stack direction="row" gap={1}>
                <TextField
                  fullWidth
                  placeholder="Nowa pozycja…"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                  inputProps={{ maxLength: 180, 'aria-label': 'Nowa pozycja checklisty' }}
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addChecklistItem} disabled={!newChecklistItem.trim()} sx={{ flexShrink: 0 }}>
                  Dodaj
                </Button>
              </Stack>
            )}
          </SectionCard>
        </Stack>

        <Stack gap={GAP} sx={{ gridArea: 'meta', minWidth: 0 }}>
          <SectionCard title="Szczegóły">
            <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, mb: 1 }}>Przypisane osoby</Typography>
            {assignees.length === 0 ? (
              <Typography sx={{ fontSize: '0.9375rem', color: tokens.muted }}>Nikt nie jest przypisany.</Typography>
            ) : (
              <Stack gap={1}>
                {assignees.map((a) => (
                  <Stack key={a.id} direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
                    <PersonAvatar firstName={a.firstName} lastName={a.lastName} fallback={a.email} seed={a.id} size={34} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{userLabel(a)}</Typography>
                      {(a.firstName || a.lastName) && (
                        <Typography noWrap sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{a.email}</Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}

            <Stack gap={1.25} sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.line}` }}>
              <MetaRow label="Termin wykonania">
                {task.dueAt ? (
                  <Box component="span" sx={{ color: task.isOverdue ? tokens.danger : 'inherit' }}>
                    {formatDate(task.dueAt, task.dueHasTime)}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: tokens.muted, fontWeight: 500 }}>Bez terminu</Box>
                )}
              </MetaRow>
              <MetaRow label="Rozpoczęcie">
                {task.startAt ? formatDate(task.startAt, task.startHasTime) : (
                  <Box component="span" sx={{ color: tokens.muted, fontWeight: 500 }}>Nie ustawiono</Box>
                )}
              </MetaRow>
              <MetaRow label="Kategoria">
                {task.category ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: task.category.color, flexShrink: 0 }} />
                    {task.category.name}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: tokens.muted, fontWeight: 500 }}>Bez kategorii</Box>
                )}
              </MetaRow>
            </Stack>

            <Stack gap={1.25} sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.line}` }}>
              <MetaRow label="Autor">{userLabel(withMemberName(task.createdBy, members))}</MetaRow>
              <MetaRow label="Utworzono">{formatDateTime(task.createdAt)}</MetaRow>
              <MetaRow label="Ostatnia zmiana">{formatDateTime(task.updatedAt)}</MetaRow>
              {task.completedAt && <MetaRow label="Zakończono">{formatDateTime(task.completedAt)}</MetaRow>}
            </Stack>
          </SectionCard>

          <TaskTimerPanel
            task={task}
            currentUserId={user?.id || ''}
            onChanged={refreshAll}
            onNotify={notify}
            members={members}
          />
        </Stack>

        <Stack gap={GAP} sx={{ gridArea: 'bottom', minWidth: 0 }}>
          <TaskComments
            taskId={task.id}
            members={members}
            currentUserId={user?.id || ''}
            canComment={p.canComment}
            canManage={p.canManage}
            onChanged={refreshAll}
            onNotify={notify}
            onOpenAttachment={openAttachment}
          />

          <TaskAttachments
            taskId={task.id}
            currentUserId={user?.id || ''}
            canUpload={p.canUpload}
            canManage={p.canManage}
            onChanged={refreshAll}
            onNotify={notify}
            onOpenAttachment={openAttachment}
            members={members}
          />

          <TaskHistory key={historyKey} taskId={task.id} members={members} />
        </Stack>
      </Box>

      {/* Rework reason */}
      <Dialog open={!!reworkDialog} onClose={() => setReworkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Zwrócić zadanie do poprawy?</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline minRows={3} label="Powód zwrotu" required
            placeholder="Co trzeba poprawić?"
            value={reworkDialog?.reason || ''}
            onChange={(e) => setReworkDialog({ reason: e.target.value })}
            helperText="Co najmniej 3 znaki. Powód zapiszemy jako komentarz i w historii zadania."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setReworkDialog(null)}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={(reworkDialog?.reason.trim().length || 0) < 3}
            onClick={() => {
              const reason = reworkDialog!.reason.trim();
              setReworkDialog(null);
              changeStatus('NEEDS_REWORK', { reason });
            }}
          >
            Zwróć do poprawy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active timer confirmation */}
      <Dialog open={!!timerDialog} onClose={() => setTimerDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zatrzymać pomiar czasu?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens.inkSoft }}>
            Do tego zadania trwa pomiar czasu. Zatrzymamy go, zapiszemy zmierzony czas
            {timerDialog ? ` i zmienimy status na „${STATUS_LABEL[timerDialog]}”.` : '.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setTimerDialog(null)}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={() => {
              const status = timerDialog!;
              setTimerDialog(null);
              changeStatus(status, { stopTimer: true });
            }}
          >
            Zatrzymaj i zmień status
          </Button>
        </DialogActions>
      </Dialog>

      <TaskFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => { refreshAll(); notify('Zapisano zmiany.', 'success'); }}
        salonId={task.salonId}
        task={task}
        members={members}
        categories={categories}
        isManager={p.canManage}
        currentUserId={user?.id || ''}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskDetailPage;
